import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { NotificationService } from '../../../services/notification.service'; // NotificationService;
import { Subject, finalize, takeUntil } from 'rxjs';
import { CashReportData, CashReportEmployee, CashReportRow } from '../../models/cash-report.model';
import { ReportsService } from '../../reports.service';
import { InfoTipComponent } from '../../../shared/components/info-tip';
import { CASH_REPORT_TIPS } from '../../report-field-definitions';

@Component({
  selector: 'app-cash-report-results',
  standalone: true,
  imports: [CommonModule, InfoTipComponent],
  templateUrl: './cash-report-results.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './cash-report-results.component.scss',
})
export class CashReportResultsComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input({ required: true }) report!: CashReportData;
  @Input({ required: true }) startDate!: string;
  @Input({ required: true }) endDate!: string;
  @Input() employeeIds: string[] | undefined;

  @ViewChild('tabsScroll') tabsScroll?: ElementRef<HTMLElement>;

  readonly tips = CASH_REPORT_TIPS;

  activeEmployeeId = '';
  canScrollLeft = false;
  canScrollRight = false;
  exportingPdf = false;
  exportingExcel = false;

  private readonly reportsService = inject(ReportsService);
  private readonly messageService = inject(NotificationService);
  private readonly destroy$ = new Subject<void>();
  private resizeObserver?: ResizeObserver;

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['report']) {
      return;
    }
    const employees = this.report?.employees ?? [];
    const stillValid = employees.some(emp => emp.employee_id === this.activeEmployeeId);
    if (!stillValid) {
      this.activeEmployeeId = employees[0]?.employee_id ?? '';
    }
    queueMicrotask(() => this.updateTabsScrollState());
  }

  ngAfterViewInit(): void {
    const el = this.tabsScroll?.nativeElement;
    if (!el || typeof ResizeObserver === 'undefined') {
      this.updateTabsScrollState();
      return;
    }
    this.resizeObserver = new ResizeObserver(() => this.updateTabsScrollState());
    this.resizeObserver.observe(el);
    this.updateTabsScrollState();
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.destroy$.next();
    this.destroy$.complete();
  }

  get activeEmployee(): CashReportEmployee | null {
    return this.report?.employees?.find(emp => emp.employee_id === this.activeEmployeeId) ?? null;
  }

  selectEmployee(employeeId: string): void {
    this.activeEmployeeId = employeeId;
  }

  onTabsScroll(): void {
    this.updateTabsScrollState();
  }

  scrollTabs(direction: 'left' | 'right'): void {
    const el = this.tabsScroll?.nativeElement;
    if (!el) {
      return;
    }
    const delta = Math.max(el.clientWidth * 0.55, 160);
    el.scrollBy({ left: direction === 'right' ? delta : -delta, behavior: 'smooth' });
    window.setTimeout(() => this.updateTabsScrollState(), 280);
  }

  exportExcel(): void {
    this.downloadExport('xlsx');
  }

  exportPdf(): void {
    this.downloadExport('pdf');
  }

  private downloadExport(format: 'pdf' | 'xlsx'): void {
    if (this.exportingPdf || this.exportingExcel || !this.startDate || !this.endDate) {
      return;
    }
    if (!this.report?.employees?.length) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Nothing to export',
        detail: 'Generate a cash report with data before exporting.',
      });
      return;
    }

    if (format === 'pdf') {
      this.exportingPdf = true;
    } else {
      this.exportingExcel = true;
    }

    const request$ =
      format === 'pdf'
        ? this.reportsService.downloadCashReportPdf({
            startDate: this.startDate,
            endDate: this.endDate,
            employee_ids: this.employeeIds,
          })
        : this.reportsService.downloadCashReportXlsx({
            startDate: this.startDate,
            endDate: this.endDate,
            employee_ids: this.employeeIds,
          });

    const ext = format === 'pdf' ? 'pdf' : 'xlsx';
    const label = format === 'pdf' ? 'PDF' : 'Excel';

    request$
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.exportingPdf = false;
          this.exportingExcel = false;
        })
      )
      .subscribe({
        next: blob => {
          const filename = `cash-reconciliation-${this.startDate}-${this.endDate}.${ext}`;
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = filename;
          link.click();
          URL.revokeObjectURL(url);
          this.messageService.add({
            severity: 'success',
            summary: 'Export ready',
            detail: `${label} file downloaded.`,
          });
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: 'Export failed',
            detail: `Unable to download the ${label} report. Please try again.`,
          });
        },
      });
  }

  formatMoney(value: number | null | undefined, decimals = 2): string {
    const amount = Number(value ?? 0);
    if (!Number.isFinite(amount)) {
      return '0.00';
    }
    return amount.toLocaleString('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  }

  formatKpiMoney(value: number | null | undefined): string {
    const amount = Number(value ?? 0);
    if (!Number.isFinite(amount)) {
      return '$0';
    }
    if (Number.isInteger(amount)) {
      return `$${amount.toLocaleString('en-US')}`;
    }
    return `$${this.formatMoney(amount)}`;
  }

  formatCellMoney(value: number | null | undefined): string {
    if (value === null || value === undefined) {
      return '$0.00';
    }
    return `$${this.formatMoney(value)}`;
  }

  trackByEmployeeId(_index: number, emp: CashReportEmployee): string {
    return emp.employee_id;
  }

  trackByRowId(_index: number, row: CashReportRow): string {
    return row.id;
  }

  private updateTabsScrollState(): void {
    const el = this.tabsScroll?.nativeElement;
    if (!el) {
      this.canScrollLeft = false;
      this.canScrollRight = false;
      return;
    }
    const maxScroll = el.scrollWidth - el.clientWidth;
    this.canScrollLeft = el.scrollLeft > 2;
    this.canScrollRight = maxScroll > 2 && el.scrollLeft < maxScroll - 2;
  }
}
